import {
    createFileRoute,
    Link,
    useRouter,
    useRouterState,
} from '@tanstack/react-router';
import { ChevronLeft, MessageSquareOff } from 'lucide-react';
import {
    Fragment,
    lazy,
    type ReactNode,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { z } from 'zod';

import { AgentChatEntityDetails } from '@apps/web/components/agent_chat_entity_details';
import { PhoneAppBarButton } from '@apps/web/components/layout/phone_app_bar_slots';
import { PhoneChatHeader } from '@apps/web/components/layout/phone_chat_header';
import { requestDebugTimelineFocus } from '@apps/web/components/phoebe_chat_debug_focus';
import { Button } from '@apps/web/components/ui/button';
import { EmptyState } from '@apps/web/components/ui/empty_state';
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from '@apps/web/components/ui/resizable';
import { V3RunDebugSidebar } from '@apps/web/components/v3_run_debug_sidebar';
import { DisplayNamesLastFirstProvider } from '@apps/web/context/display_names_last_first';
import { useSessionState } from '@apps/web/context/session_state_provider';
import {
    RunApiError,
    useRunSummary,
} from '@apps/web/features/phoebe_chat_v3/api/run_api';
import { type HarnessScope } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { resolveScopeTimeZone } from '@apps/web/features/phoebe_chat_v3/model/scope_time_zone';
import { type RunSummary } from '@apps/web/features/phoebe_chat_v3/profiles/agent_profile';
import { ConversationLoading } from '@apps/web/features/phoebe_chat_v3/ui/primitives/conversation_loading';
import {
    chatKindForRun,
    useCaptureRunDeepLinkOpen,
    V3AnalyticsDimensionsProvider,
} from '@apps/web/features/phoebe_chat_v3/ui/v3_analytics';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { useUser } from '@apps/web/swr_data_fetching/current_user_queries';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';
import { ApiError } from '@libraries/typescript/api_client';

const V3_PROFILE_RESOLVER_ID = 'v3-phoebe-general';
const DEFAULT_RUN_PANEL_SIZE = 62;
const DEFAULT_DEBUG_PANEL_SIZE = 38;
const MIN_DEBUG_PANEL_SIZE = 20;
const RUN_ID_SCHEMA = z.uuid();
export const TRANSCRIPT_ROUTE_HISTORY_STATE_KEY =
    'phoebeV3TranscriptRestoration';

export type TranscriptScopeKey = {
    readonly userId: string;
    readonly organizationId: string;
    readonly mode: 'live' | 'sandbox';
    readonly timezone: string;
    readonly profileId: string;
    readonly rendererVersion: string;
    readonly runId: string;
};

export type TranscriptRouteHistoryState = {
    readonly scopeKey: TranscriptScopeKey;
    readonly committedEventId: string;
    readonly intraRowOffset: number;
    readonly expandedGroupIds: Array<string>;
};

type TranscriptRestorationFields = Readonly<{
    readonly committedEventId: string;
    readonly intraRowOffset: number;
    readonly expandedGroupIds: Array<string>;
}>;

type TranscriptPositionCapture = Readonly<{
    readonly capture: () => TranscriptRestorationFields | null;
}>;

type CurrentNavigationEntryHistory = Readonly<{
    readonly location: Readonly<{
        readonly href: string;
        readonly state: unknown;
    }>;
    readonly replace: (
        href: string,
        state: Readonly<Record<string, unknown>>,
    ) => void;
}>;

const TRANSCRIPT_SCOPE_KEY_SCHEMA = z
    .object({
        userId: z.string().min(1),
        organizationId: z.string().min(1),
        mode: z.enum(['live', 'sandbox']),
        timezone: z.string().min(1),
        profileId: z.string().min(1),
        rendererVersion: z.string().min(1),
        runId: z.string().min(1),
    })
    .strict();

const TRANSCRIPT_ROUTE_HISTORY_STATE_SCHEMA = z
    .object({
        scopeKey: TRANSCRIPT_SCOPE_KEY_SCHEMA,
        committedEventId: z.string().min(1),
        intraRowOffset: z.number().finite().nonnegative(),
        expandedGroupIds: z.array(z.string().min(1)),
    })
    .strict();

const HISTORY_ENTRY_STATE_SCHEMA = z.record(z.string(), z.unknown());

function sortedGroupIds(groupIds: Array<string>): Array<string> {
    return [...groupIds].sort();
}

function normalizeTranscriptRouteHistoryState(
    state: TranscriptRouteHistoryState,
): TranscriptRouteHistoryState {
    return {
        ...state,
        expandedGroupIds: sortedGroupIds(state.expandedGroupIds),
    };
}

export function validateTranscriptRouteHistoryState(
    historyState: unknown,
): TranscriptRouteHistoryState | null {
    const entryState = HISTORY_ENTRY_STATE_SCHEMA.safeParse(historyState);
    if (!entryState.success) {
        return null;
    }
    const descriptor = TRANSCRIPT_ROUTE_HISTORY_STATE_SCHEMA.safeParse(
        entryState.data[TRANSCRIPT_ROUTE_HISTORY_STATE_KEY],
    );
    if (!descriptor.success) {
        return null;
    }
    return normalizeTranscriptRouteHistoryState(descriptor.data);
}

export function transcriptScopeKeysEqual(
    left: TranscriptScopeKey,
    right: TranscriptScopeKey,
): boolean {
    return (
        left.userId === right.userId &&
        left.organizationId === right.organizationId &&
        left.mode === right.mode &&
        left.timezone === right.timezone &&
        left.profileId === right.profileId &&
        left.rendererVersion === right.rendererVersion &&
        left.runId === right.runId
    );
}

export function readScopedTranscriptRouteHistoryState(
    historyState: unknown,
    scopeKey: TranscriptScopeKey,
): TranscriptRouteHistoryState | null {
    const descriptor = validateTranscriptRouteHistoryState(historyState);
    if (
        descriptor === null ||
        !transcriptScopeKeysEqual(descriptor.scopeKey, scopeKey)
    ) {
        return null;
    }
    return descriptor;
}

export function mergeTranscriptRouteHistoryState(
    historyState: unknown,
    descriptor: TranscriptRouteHistoryState,
): Readonly<Record<string, unknown>> {
    const entryState = HISTORY_ENTRY_STATE_SCHEMA.safeParse(historyState);
    return {
        ...(entryState.success ? entryState.data : {}),
        [TRANSCRIPT_ROUTE_HISTORY_STATE_KEY]:
            normalizeTranscriptRouteHistoryState(descriptor),
    };
}

function transcriptRouteHistoryStatesEqual(
    left: TranscriptRouteHistoryState,
    right: TranscriptRouteHistoryState,
): boolean {
    if (
        !transcriptScopeKeysEqual(left.scopeKey, right.scopeKey) ||
        left.committedEventId !== right.committedEventId ||
        left.intraRowOffset !== right.intraRowOffset ||
        left.expandedGroupIds.length !== right.expandedGroupIds.length
    ) {
        return false;
    }
    return left.expandedGroupIds.every(
        (groupId, index) => groupId === right.expandedGroupIds[index],
    );
}

export function replaceTranscriptRouteHistoryState(
    history: CurrentNavigationEntryHistory,
    descriptor: TranscriptRouteHistoryState,
): boolean {
    const normalizedDescriptor =
        normalizeTranscriptRouteHistoryState(descriptor);
    const currentDescriptor = validateTranscriptRouteHistoryState(
        history.location.state,
    );
    if (
        currentDescriptor !== null &&
        transcriptRouteHistoryStatesEqual(
            currentDescriptor,
            normalizedDescriptor,
        )
    ) {
        return false;
    }
    history.replace(
        history.location.href,
        mergeTranscriptRouteHistoryState(
            history.location.state,
            normalizedDescriptor,
        ),
    );
    return true;
}

function restorationFields(
    descriptor: TranscriptRouteHistoryState,
): TranscriptRestorationFields {
    return {
        committedEventId: descriptor.committedEventId,
        intraRowOffset: descriptor.intraRowOffset,
        expandedGroupIds: descriptor.expandedGroupIds,
    };
}

type V3RunBranchProps = Readonly<{
    readonly invalidOrganizationTimeZone: boolean;
    readonly scope: HarnessScope;
    readonly summary: RunSummary;
}>;

const V3RunBranch = lazy(async function loadV3RunBranch() {
    const feature = await import('@apps/web/features/phoebe_chat_v3');
    function ResolvedV3RunBranch({
        invalidOrganizationTimeZone,
        scope,
        summary,
    }: V3RunBranchProps) {
        const router = useRouter();
        const isPhone = useIsMobile();
        const analyticsDimensions = useMemo(
            () => ({
                chatKind: chatKindForRun(summary),
                panelMode: null,
            }),
            [summary],
        );
        const { data: user } = useUser({ reason: 'v3-run-debug-rail' });
        const { debug: debugRequested } = Route.useSearch();
        const isAdmin = user?.admin === true;
        const [debugRailOpen, setDebugRailOpen] = useState(
            debugRequested === true,
        );
        const toggleDebugRail = useCallback(
            () => setDebugRailOpen((open) => !open),
            [],
        );
        const openMessageDebug = useCallback(
            (eventId: string) => {
                setDebugRailOpen(true);
                requestDebugTimelineFocus(eventId, scope.runId, 'run-page');
            },
            [scope.runId],
        );
        const navigationEntryIndex = useRouterState({
            select: (state) => state.location.state.__TSR_index,
        });
        const restorationEntryKey =
            typeof navigationEntryIndex === 'number' &&
            Number.isInteger(navigationEntryIndex) &&
            navigationEntryIndex >= 0
                ? navigationEntryIndex
                : null;
        const captureRef = useRef<TranscriptPositionCapture | null>(null);
        const scopeKey = useMemo<TranscriptScopeKey>(
            () => ({
                userId: scope.userId,
                organizationId: scope.organizationId,
                mode: scope.mode,
                timezone: scope.organizationTimeZone,
                profileId: feature.V3_PHOEBE_PROFILE_ID,
                rendererVersion: feature.V3_PHOEBE_RENDERER_VERSION,
                runId: scope.runId,
            }),
            [
                scope.mode,
                scope.organizationId,
                scope.organizationTimeZone,
                scope.runId,
                scope.userId,
            ],
        );
        const currentEntryHistory = useMemo<CurrentNavigationEntryHistory>(
            () => ({
                get location() {
                    return router.history.location;
                },
                replace(href, state) {
                    router.history.replace(href, state, {
                        ignoreBlocker: true,
                    });
                },
            }),
            [router],
        );
        const initialDescriptor = useMemo(() => {
            if (restorationEntryKey === null) {
                return null;
            }
            const descriptor = readScopedTranscriptRouteHistoryState(
                currentEntryHistory.location.state,
                scopeKey,
            );
            return descriptor === null ? null : restorationFields(descriptor);
        }, [currentEntryHistory, restorationEntryKey, scopeKey]);
        const replacePosition = useCallback(
            (descriptor: TranscriptRestorationFields): boolean =>
                replaceTranscriptRouteHistoryState(currentEntryHistory, {
                    scopeKey,
                    ...descriptor,
                }),
            [currentEntryHistory, scopeKey],
        );
        const onPositionChange = useCallback(
            (descriptor: TranscriptRestorationFields) => {
                replacePosition(descriptor);
            },
            [replacePosition],
        );
        const captureAndFlushPosition = useCallback(() => {
            const descriptor = captureRef.current?.capture() ?? null;
            if (descriptor !== null) {
                replacePosition(descriptor);
            }
            router.history.flush();
        }, [replacePosition, router]);

        useEffect(() => {
            function handlePageHide() {
                captureAndFlushPosition();
            }
            window.addEventListener('pagehide', handlePageHide);
            return () => {
                window.removeEventListener('pagehide', handlePageHide);
            };
        }, [captureAndFlushPosition]);

        useEffect(
            () =>
                router.history.block({
                    enableBeforeUnload: false,
                    blockerFn() {
                        // A navigation-entry change remounts transcript
                        // positioning and cancels its debounce. Persist the
                        // latest capture before every entry transition.
                        captureAndFlushPosition();
                        return false;
                    },
                }),
            [captureAndFlushPosition, router],
        );

        const navigateBack = () => {
            if (router.history.canGoBack()) {
                router.history.back();
                return;
            }
            void router.navigate({ to: '/home' });
        };
        const overflowMenu = (
            <feature.ChatOverflowMenu
                run={{
                    id: scope.runId,
                    agentConversationId: summary.agent_conversation_id,
                    organizationId: scope.organizationId,
                    mode: scope.mode,
                }}
                adminTools={
                    isAdmin
                        ? {
                              debugPanelOpen: debugRailOpen,
                              onToggleDebugPanel: toggleDebugRail,
                          }
                        : undefined
                }
                triggerClassName={isPhone ? 'size-11' : undefined}
            />
        );
        const runColumn = (
            <div className="mx-auto flex h-full min-h-0 w-full max-w-[960px] flex-col">
                <AgentChatEntityDetails
                    organizationId={scope.organizationId}
                    mode={scope.mode}
                    timezone={scope.organizationTimeZone}
                >
                    <feature.MessageDebugContext.Provider
                        value={isAdmin ? openMessageDebug : null}
                    >
                        <DisplayNamesLastFirstProvider
                            organizationId={scope.organizationId}
                        >
                            <V3AnalyticsDimensionsProvider
                                value={analyticsDimensions}
                            >
                                <feature.V3RunSurface
                                    captureRef={captureRef}
                                    differentExperience={
                                        <RunRouteUnexpectedError />
                                    }
                                    initialDescriptor={initialDescriptor}
                                    invalidOrganizationTimeZone={
                                        invalidOrganizationTimeZone
                                    }
                                    onPositionChange={onPositionChange}
                                    restorationEntryKey={restorationEntryKey}
                                    scope={scope}
                                    summary={summary}
                                    viewerUserId={user?.id ?? null}
                                />
                            </V3AnalyticsDimensionsProvider>
                        </DisplayNamesLastFirstProvider>
                    </feature.MessageDebugContext.Provider>
                </AgentChatEntityDetails>
            </div>
        );
        return (
            <div
                data-testid="v3-run-workspace"
                className="bg-background fixed inset-0 flex h-svh min-h-0 min-w-0 flex-col overflow-hidden md:pt-3.5"
            >
                {isPhone ? (
                    <PhoneChatHeader
                        leading={
                            <PhoneAppBarButton
                                label="Back"
                                onClick={navigateBack}
                            >
                                <ChevronLeft className="size-6" />
                            </PhoneAppBarButton>
                        }
                        title={summary.title?.trim() || 'Conversation'}
                        trailing={overflowMenu}
                    />
                ) : null}
                {isAdmin ? (
                    // Admins keep the run inside a panel group even while the
                    // debug rail is closed, so toggling the rail never
                    // remounts the transcript (a remount drops its scroll
                    // position).
                    <ResizablePanelGroup
                        orientation="horizontal"
                        className="h-full min-h-0 w-full overflow-hidden"
                    >
                        <ResizablePanel
                            id="run-panel"
                            defaultSize={DEFAULT_RUN_PANEL_SIZE}
                            className="min-h-0 overflow-hidden"
                        >
                            {runColumn}
                        </ResizablePanel>
                        {debugRailOpen && (
                            <Fragment>
                                <ResizableHandle withHandle={true} />
                                <ResizablePanel
                                    id="debug-panel"
                                    defaultSize={DEFAULT_DEBUG_PANEL_SIZE}
                                    minSize={MIN_DEBUG_PANEL_SIZE}
                                    className="min-h-0 overflow-hidden"
                                >
                                    <V3RunDebugSidebar
                                        focusTarget="run-page"
                                        runId={scope.runId}
                                        organizationId={scope.organizationId}
                                        mode={scope.mode}
                                        title={summary.title ?? undefined}
                                    />
                                </ResizablePanel>
                            </Fragment>
                        )}
                    </ResizablePanelGroup>
                ) : (
                    runColumn
                )}
                {isPhone ? null : (
                    <div className="absolute top-2 right-3 z-10">
                        {overflowMenu}
                    </div>
                )}
            </div>
        );
    }
    return { default: ResolvedV3RunBranch };
});

export const agentRunSearchSchema = z
    .object({
        organizationId: z.uuid(),
        mode: z.enum(['live', 'sandbox']),
        // Admin deep-link: open the run with the debug rail already showing.
        debug: z.boolean().optional(),
    })
    .strip();

export const Route = createFileRoute('/_app/(agent)/agent/runs/$runId')({
    component: AgentRunPage,
    errorComponent: RunRouteError,
    validateSearch: agentRunSearchSchema,
    head: () => ({
        meta: [
            {
                title: 'Conversation | Phoebe',
            },
        ],
    }),
});

function AgentRunPage() {
    const { runId } = Route.useParams();
    const search = Route.useSearch();
    const { session } = useSessionState();
    const parsedRunId = RUN_ID_SCHEMA.safeParse(runId);

    if (!parsedRunId.success) {
        return <RunRouteUnavailable />;
    }
    if (session === null) {
        return <RunRouteError error={new RunApiError('unauthorized')} />;
    }
    return (
        <RunOrganizationResolution
            mode={search.mode}
            organizationId={search.organizationId}
            runId={parsedRunId.data}
            userId={session.identity.id}
        />
    );
}

type RunOrganizationResolutionProps = Readonly<{
    readonly mode: 'live' | 'sandbox';
    readonly organizationId: string;
    readonly runId: string;
    readonly userId: string;
}>;

function RunOrganizationResolution({
    mode,
    organizationId,
    runId,
    userId,
}: RunOrganizationResolutionProps) {
    const { data: organization, error } = useOrganization(
        organizationId,
        { reason: 'v3-agent-run-route' },
        mode,
    );
    const timeZoneResolution = useMemo(
        () => resolveScopeTimeZone(organization?.timezone),
        [organization?.timezone],
    );

    if (error !== undefined) {
        throw error;
    }
    if (organization === undefined) {
        return <RunRoutePending />;
    }
    const scope: HarnessScope = {
        userId,
        organizationId,
        organizationName: organization.name,
        mode,
        runId,
        organizationTimeZone: timeZoneResolution.timeZone,
    };
    return (
        <RunEligibilityResolution
            invalidOrganizationTimeZone={timeZoneResolution.invalid}
            scope={scope}
        />
    );
}

function RunEligibilityResolution({
    invalidOrganizationTimeZone,
    scope,
}: {
    readonly invalidOrganizationTimeZone: boolean;
    readonly scope: HarnessScope;
}) {
    const summaryState = useRunSummary({
        ...scope,
        profileResolverId: V3_PROFILE_RESOLVER_ID,
    });
    useCaptureRunDeepLinkOpen(
        scope.runId,
        scope.mode,
        summaryState.kind === 'loading'
            ? null
            : summaryState.summary.is_agent_v3 === true,
    );
    if (summaryState.kind === 'loading') {
        return <RunRoutePending />;
    }
    if (summaryState.summary.is_agent_v3 === true) {
        const routeScopeKey = JSON.stringify([
            scope.userId,
            scope.organizationId,
            scope.mode,
            scope.runId,
            scope.organizationTimeZone,
        ]);
        return (
            <Suspense fallback={<RunRoutePending />}>
                <V3RunBranch
                    key={routeScopeKey}
                    invalidOrganizationTimeZone={invalidOrganizationTimeZone}
                    scope={scope}
                    summary={summaryState.summary}
                />
            </Suspense>
        );
    }
    if (summaryState.summary.is_agent_v3 === false) {
        return (
            <RunRouteDifferentExperience
                mode={scope.mode}
                organizationId={scope.organizationId}
                runId={scope.runId}
            />
        );
    }
    return <RunRouteUnexpectedError />;
}

function RunRoutePending() {
    return <ConversationLoading />;
}

function RunRouteMessage({
    title,
    action,
}: {
    readonly title: string;
    readonly action?: ReactNode;
}) {
    return (
        <div className="flex min-h-svh items-center justify-center px-4 pt-(--safe-top) pb-(--safe-bottom)">
            <EmptyState
                icon={action === undefined ? undefined : MessageSquareOff}
                title={title}
                action={action}
            />
        </div>
    );
}

function GoHomeAction() {
    return (
        <Button asChild={true} variant="outline">
            <Link to="/home">Go to home</Link>
        </Button>
    );
}

function RunRouteSigningIn() {
    return <RunRouteMessage title="Signing you in…" />;
}

function RunRouteUnavailable() {
    return (
        <RunRouteMessage
            title="This conversation is not available."
            action={<GoHomeAction />}
        />
    );
}

function RunRouteUnexpectedError() {
    return (
        <RunRouteMessage
            title="Something went wrong opening this conversation. Please try again."
            action={<GoHomeAction />}
        />
    );
}

type RunRouteDifferentExperienceProps = Readonly<{
    readonly mode: 'live' | 'sandbox';
    readonly organizationId: string;
    readonly runId: string;
}>;

function RunRouteDifferentExperience({
    mode,
    organizationId,
    runId,
}: RunRouteDifferentExperienceProps) {
    return (
        <RunRouteMessage
            title="This conversation uses a different chat experience."
            action={
                <Button asChild={true} variant="outline">
                    <Link
                        to="/agent"
                        search={{ chatId: runId, mode, organizationId }}
                        replace={true}
                    >
                        Open the legacy chat experience
                    </Link>
                </Button>
            }
        />
    );
}

function RunRouteError({ error }: { readonly error: unknown }) {
    if (error instanceof ApiError) {
        if (error.status === 401) {
            return <RunRouteSigningIn />;
        }
        if (error.status === 403 || error.status === 404) {
            return <RunRouteUnavailable />;
        }
        return <RunRouteUnexpectedError />;
    }
    if (error instanceof RunApiError) {
        switch (error.kind) {
            case 'unauthorized':
                return <RunRouteSigningIn />;
            case 'forbidden':
            case 'not_found':
                return <RunRouteUnavailable />;
            case 'aborted':
            case 'network':
            case 'rejected':
            case 'unknown':
                return <RunRouteUnexpectedError />;
            default:
                return <RunRouteUnexpectedError />;
        }
    }
    return <RunRouteUnexpectedError />;
}
